import React, { useCallback, useEffect, useState, Fragment, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, BookOpenIcon, ClockIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, HeartIcon, UsersIcon, CheckCircleIcon, BriefcaseIcon, DollarSignIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { getGuideBySlug } from '../data/guides';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  ClockIcon,
  TrendingUpIcon,
  GlobeIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  HeartIcon,
  UsersIcon,
  CheckCircleIcon
};
export function GuidePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;
  usePageMeta(guide?.metaTitle || 'Guide | OnlineCollegeGuide', guide?.metaDescription || 'Explore accredited online colleges and degree programs.', {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'article'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: guide?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load with the guide's focus value
  useEffect(() => {
    if (guide?.focusValue) {
      fetchResults('', guide.focusValue, guide.defaultSpecialization || '');
    }
  }, [guide?.focusValue, guide?.defaultSpecialization, fetchResults]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  if (!guide) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1194">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1195">Guide Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1196">
          We couldn't find the guide you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1197">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1198">
      {/* Page Header */}
      <div className="bg-navy text-white py-12" data-id="element-1199">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1200">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1201">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1202">
              <li className="inline-flex items-center" data-id="element-1203">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1204">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1205" />
                  Home
                </Link>
              </li>
              <li data-id="element-1206">
                <div className="flex items-center" data-id="element-1207">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1208" />
                  <span className="ml-1 md:ml-2" data-id="element-1209">Guides</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1210">
                <div className="flex items-center" data-id="element-1211">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1212" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1213">
                    {guide.breadcrumbLabel}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          {guide.heroEyebrow && <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-4 text-[#D4A943] uppercase tracking-widest" data-id="element-1214">
              <CrossIcon className="w-3.5 h-3.5 mr-2" data-id="element-1215" />
              {guide.heroEyebrow}
            </div>}

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-1216">
            {guide.heroHeadline}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-1217">
            {guide.heroDescription}
          </p>
        </div>
      </div>

      <TrustSignals data-id="element-1218" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1219">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1220">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-2 lg:order-1" data-id="element-1221">
            <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1222">
              Top {guide.title} of {new Date().getFullYear()}
            </h2>

            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1223">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1224" />
                <p className="text-gray-600 font-medium" data-id="element-1225">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1226">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1227" />
                <div data-id="element-1228">
                  <p className="text-red-800 font-medium" data-id="element-1229">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1230">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1231">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1232">
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1233" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1234" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1235">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1236">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1237" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1238">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1239">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1240">
            <div className="sticky top-24 space-y-6" data-id="element-1241">
              {/* Lead Form */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-1242">
                <div className="bg-[#1B365D] px-6 py-4" data-id="element-1243">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1244">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1245">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1246">
                  <div data-id="element-1247">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1248">
                      <option value="" data-id="element-1249">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1250">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1251">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1252">
                      <option value="" data-id="element-1253">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1254">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1255">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1256">
                      <option value="" data-id="element-1257">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1258">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-[#D4A943] hover:bg-[#B38B2E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1259">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1260" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* What to Look For */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1261">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1262">
                  {guide.sidebarChecklistTitle}
                </h4>
                <ul className="space-y-2.5" data-id="element-1263">
                  {guide.sidebarChecklist.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-1264">
                      <CheckCircleIcon className="w-4 h-4 text-[#059668] flex-shrink-0 mt-0.5" data-id="element-1265" />
                      <span data-id="element-1266">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Related Links */}
              {guide.relatedLinks.length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1267">
                  <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1268">
                    Related
                  </h4>
                  <ul className="space-y-2" data-id="element-1269">
                    {guide.relatedLinks.map(link => <li key={link.href} data-id="element-1270">
                        <Link to={link.href} className="text-sm flex items-center text-gray-600 hover:text-crimson" data-id="element-1271">
                          <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1272" />
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Guide Content Sections */}
      <div className="bg-white border-t border-gray-200 mt-12 pt-16 pb-20" data-id="element-1273">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1274">
          {/* Overview */}
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1275">
            <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-1276">
              {guide.overviewHeading}
            </h2>
            <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6 text-left" data-id="element-1277">
              {guide.overviewParagraphs.map((p, i) => <p key={i} data-id="element-1278">{p}</p>)}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-1279">
            <div className="text-center mb-12" data-id="element-1280">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1281">
                {guide.benefitsHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1282">
                {guide.benefitsIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1283">
              {guide.benefits.map((benefit, i) => {
              const IconComponent = benefitIconMap[benefit.icon] || BookOpenIcon;
              return <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1284">
                    <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1285">
                      <IconComponent className="w-6 h-6 text-[#1B365D]" data-id="element-1286" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1287">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1288">
                      {benefit.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Careers */}
          <div className="mb-20" data-id="element-1289">
            <div className="text-center mb-10" data-id="element-1290">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1291">
                {guide.careersHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1292">
                {guide.careersIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1293">
              {guide.careers.map((career, i) => <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full" data-id="element-1294">
                  <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1295">
                    <BriefcaseIcon className="w-6 h-6" data-id="element-1296" />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2 leading-tight" data-id="element-1297">
                    {career.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-1298">
                    {career.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-1299">
                    <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-1300">
                      <DollarSignIcon className="w-4 h-4 text-[#059668] mr-1" data-id="element-1301" />
                      {career.salary}
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1302">
                      Estimated Annual Salary
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* FAQs */}
          <div className="max-w-3xl mx-auto" data-id="element-1303">
            <div className="text-center mb-10" data-id="element-1304">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1305">
                {guide.faqHeading}
              </h2>
              <p className="text-gray-600" data-id="element-1306">{guide.faqIntro}</p>
            </div>

            <div className="space-y-4" data-id="element-1307">
              {guide.faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-1308">
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-1309">
                    <span className="font-semibold text-navy pr-8" data-id="element-1310">
                      {faq.q}
                    </span>
                    {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1311" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1312" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-1313">
                      <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-1314">
                        {faq.a}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </main>;
}